"use client";

import React, { useCallback } from "react";
import HomeTabList from "./HomeTabList";
import FeaturedStaysRail from "./FeaturedStaysRail";
import RailDrawer from "./RailDrawer";
import ListingSlider from "@/src/shared/components/listing/ListingSlider";
import type { IHomepageRail, IHomepageTab } from "../types";
import { homepageService, type TRailMatch } from "../services";
import { useRailDrawer } from "../hooks/useRailDrawer";
import HomeRailsSkeleton from "./skeletons/HomeRailsSkeleton";
import { FROM_PAGE_ATTRIBUTE } from "@/src/shared/utils/navigationHistory";

/** Below `md`, a rail opens as a drawer; above it, its title is a plain link to its page. */
const MOBILE_QUERY = "(max-width: 767px)";

interface IHomepageSectionsProps {
  tabs: IHomepageTab[];
  selectedTab: IHomepageTab | null;
  rails: IHomepageRail[];
  isLoading: boolean;
  onSelectTab: (tab: IHomepageTab) => void;
}

/** Homepage tab strip plus the selected tab's rails; state comes from useHomepageTabs. */
const HomepageSections: React.FC<IHomepageSectionsProps> = ({
  tabs,
  selectedTab,
  rails,
  isLoading,
  onSelectTab,
}) => {
  const tabItems = tabs.map((tab) => ({ ...tab, icon: tab.icon_url ?? "" }));
  const selectedItem =
    tabItems.find((tab) => tab.slug === selectedTab?.slug) ?? null;
  const visibleRails = rails.filter((rail) => rail.listings.length > 0);

  const resolve = useCallback(
    (slug: string) =>
      selectedTab
        ? homepageService.findRail([{ tab: selectedTab, rails }], slug)
        : null,
    [selectedTab, rails],
  );
  const drawer = useRailDrawer(resolve);

  // The title stays a real link (crawlable, opens in a new tab); on a phone a plain click
  // opens the drawer instead, like the app's pushed screen.
  const openRail =
    (match: TRailMatch) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      const plainClick =
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        event.button === 0;
      if (plainClick && window.matchMedia(MOBILE_QUERY).matches) {
        event.preventDefault();
        drawer.open(match);
      }
    };

  return (
    <>
      <HomeTabList
        tabs={tabItems}
        selectedTab={selectedItem}
        onSelectTab={(tab) => onSelectTab(tab)}
      />

      <div className="bg-white">
        {/* Airbnb's home width: up to 1440px, 32px in from the sides, 48px from 1440px. */}
        <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-6 lg:px-8 min-[1440px]:px-12">
          {selectedTab && (
            <FeaturedStaysRail
              key={selectedTab.slug}
              tab={selectedTab}
              href={homepageService.railHref(
                homepageService.featuredSlug(selectedTab),
              )}
              onOpen={openRail({ kind: "featured", tab: selectedTab })}
            />
          )}
          {isLoading ? (
            <HomeRailsSkeleton />
          ) : visibleRails.length === 0 ? (
            <p className="py-16 text-center text-ink-gray">
              No stays to show in {selectedTab?.title ?? "this tab"} right now.
            </p>
          ) : (
            visibleRails.map((rail) => (
              <div
                key={rail.id}
                className="mb-8"
                {...(selectedTab && {
                  [FROM_PAGE_ATTRIBUTE]: homepageService.railFromPage(
                    selectedTab,
                    rail,
                  ),
                })}
              >
                <ListingSlider
                  homeRow
                  title={rail.title}
                  subtitle={rail.subtitle}
                  listings={rail.listings.map(
                    homepageService.railCardToListing,
                  )}
                  href={
                    selectedTab
                      ? homepageService.railHref(homepageService.railSlug(rail))
                      : undefined
                  }
                  onOpen={
                    selectedTab
                      ? openRail({ kind: "rail", tab: selectedTab, rail })
                      : undefined
                  }
                />
              </div>
            ))
          )}
        </div>
      </div>

      <RailDrawer match={drawer.match} onClose={drawer.close} />
    </>
  );
};

export default HomepageSections;
